import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import SearchIcon from "@mui/icons-material/Search";

import { TextField, MenuItem, Chip, Checkbox } from "@mui/material";

/**
 * Component for handling input reference
 * @param {Object} props - Component props
 * @param {Function} props.inputRef - Reference to input element
 * @returns {React.ReactElement} Input component
 */
const inputComponent = ({ inputRef, ...props }) => <div ref={inputRef} {...props} />;

inputComponent.propTypes = {
  /**
   * Reference to the input element
   */
  inputRef: PropTypes.func
};

/**
 * Custom Control component for AsyncSelect
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Control component
 */
export const Control = ({ selectProps, innerRef, children, innerProps }) => (
  <TextField
    fullWidth
    variant="outlined"
    InputProps={{
      inputComponent,
      inputProps: {
        className: selectProps.classes?.input || "",
        inputRef: innerRef,
        children,
        ...innerProps
      }
    }}
    {...selectProps.textFieldProps}
    value={selectProps.value}
  />
);

Control.propTypes = {
  /**
   * Props passed from the select component
   */
  selectProps: PropTypes.object,
  /**
   * Reference to inner element
   */
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * Child elements
   */
  children: PropTypes.node,
  /**
   * Props passed to inner element
   */
  innerProps: PropTypes.object
};

/**
 * Custom Option component for AsyncSelect
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Option component
 */
export const Option = ({
  selectProps,
  innerRef,
  isFocused,
  isSelected,
  innerProps,
  children,
  isMulti
}) => (
  <MenuItem
    buttonRef={innerRef}
    selected={isFocused}
    component="div"
    className={selectProps.classes?.menuItem || ""}
    style={{
      fontWeight: isSelected ? 500 : 400
    }}
    {...innerProps}
  >
    {children}

    {isMulti && <Checkbox color="default" checked={isSelected} />}
  </MenuItem>
);

Option.propTypes = {
  /**
   * Props passed from the select component
   */
  selectProps: PropTypes.object,
  /**
   * Reference to inner element
   */
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * Whether the option is focused
   */
  isFocused: PropTypes.bool,
  /**
   * Whether the option is selected
   */
  isSelected: PropTypes.bool,
  /**
   * Props passed to inner element
   */
  innerProps: PropTypes.object,
  /**
   * Child elements
   */
  children: PropTypes.node,
  /**
   * Whether multiple selection is enabled
   */
  isMulti: PropTypes.bool
};

/**
 * Custom IndicatorsContainer component for AsyncSelect
 * @param {Object} props - Component props
 * @returns {React.ReactElement} IndicatorsContainer component
 */
export const IndicatorsContainer = ({ selectProps }) => (
  <div className={selectProps.classes?.indicator || ""}>
    <SearchIcon />
    <IndicatorSeparator selectProps={selectProps} />
  </div>
);

IndicatorsContainer.propTypes = {
  /**
   * Props passed from the select component
   */
  selectProps: PropTypes.object
};

/**
 * Custom IndicatorSeparator component for AsyncSelect
 * @param {Object} props - Component props
 * @returns {React.ReactElement} IndicatorSeparator component
 */
const IndicatorSeparator = ({ selectProps }) => (
  <span className={selectProps.classes?.indicatorSeparator || ""} />
);

IndicatorSeparator.propTypes = {
  /**
   * Props passed from the select component
   */
  selectProps: PropTypes.object
};

/**
 * Custom MultiValue component for AsyncSelect
 * @param {Object} props - Component props
 * @returns {React.ReactElement} MultiValue component
 */
export const MultiValue = ({ selectProps, children, isFocused, removeProps }) => (
  <Chip
    tabIndex={-1}
    label={children}
    className={classNames(selectProps.classes?.chip || "", {
      [selectProps.classes?.chipFocused || ""]: isFocused
    })}
    onDelete={event => {
      removeProps.onClick();
      removeProps.onMouseDown(event);
    }}
  />
);

MultiValue.propTypes = {
  /**
   * Props passed from the select component
   */
  selectProps: PropTypes.object,
  /**
   * Child elements
   */
  children: PropTypes.node,
  /**
   * Whether the value is focused
   */
  isFocused: PropTypes.bool,
  /**
   * Props for the remove button
   */
  removeProps: PropTypes.shape({
    onClick: PropTypes.func,
    onMouseDown: PropTypes.func
  })
};
